// Which key a pitch-class histogram sits in: Krumhansl–Kessler profiles
// correlated against the histogram rotated to each of the 24 keys. Shared by
// the MIDI estimate (an exact histogram from the notes) and the audio one
// (a chroma from the spectrum), so both suggest the same `key:` tag.

import { keyTag, type KeyMode } from "@shared/library-tags.js";

/** Krumhansl & Kessler (1982) tonal hierarchies, C as the tonic. */
const MAJOR = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

export interface KeyEstimate {
  root: number;
  mode: KeyMode;
  /** How clearly the winner beats the runner-up (0..~0.5). */
  confidence: number;
  /** The `key:…` tag for it. */
  tag: string;
}

function pearson(a: readonly number[], b: readonly number[]): number {
  const n = a.length;
  let sa = 0, sb = 0;
  for (let i = 0; i < n; i++) { sa += a[i]!; sb += b[i]!; }
  const ma = sa / n, mb = sb / n;
  let num = 0, da = 0, db = 0;
  for (let i = 0; i < n; i++) {
    const x = a[i]! - ma, y = b[i]! - mb;
    num += x * y; da += x * x; db += y * y;
  }
  return da === 0 || db === 0 ? 0 : num / Math.sqrt(da * db);
}

/**
 * The best-fitting key for a 12-bin histogram (C first), or null when the
 * histogram is empty or too flat to say. `tonicBias` nudges the relative
 * major/minor tie: a histogram whose heaviest class is the minor tonic
 * reads as minor, as a listener would hear it.
 */
export function bestKey(histogram: readonly number[], minConfidence = 0.04): KeyEstimate | null {
  if (histogram.length !== 12) return null;
  const total = histogram.reduce((n, v) => n + v, 0);
  if (!(total > 0)) return null;
  const scored: Array<{ root: number; mode: KeyMode; score: number }> = [];
  for (let root = 0; root < 12; root++) {
    const rotated = histogram.map((_, i) => histogram[(i + root) % 12]!);
    // The heaviest class is usually the tonic: a relative major/minor pair
    // shares every note, and this is what tells them apart.
    const weight = histogram[root]! / total;
    scored.push({ root, mode: "major", score: pearson(rotated, MAJOR) + weight * 0.2 });
    scored.push({ root, mode: "minor", score: pearson(rotated, MINOR) + weight * 0.2 });
  }
  scored.sort((a, b) => b.score - a.score);
  const [best, next] = scored;
  if (!best || !next) return null;
  const confidence = best.score - next.score;
  if (!(best.score > 0.5) || confidence < minConfidence) return null;
  return { root: best.root, mode: best.mode, confidence, tag: keyTag(best.root, best.mode) };
}
